import { Link, router } from "@inertiajs/react";
import { useState } from "react";
import CoverageAccordionHeader from "@/components/domains/CoverageAccordionHeader";
import CoverageAuditDialog, { type CoverageAuditDialogStep } from "@/components/domains/CoverageAuditDialog";
import CoverageAuditQuotaDialog from "@/components/domains/CoverageAuditQuotaDialog";
import PageStatusLabel from "@/components/domains/PageStatusLabel";
import IndexPagination from "@/components/IndexPagination";
import type { DomainPage, IndexPagination as IndexPaginationData, SubscriptionTier } from "@/types";
import { domainShowPartialVisit, inertiaVisitFlash } from "@/utils/domainShowReload";

type Props = {
  domainId: string;
  pages: DomainPage[];
  summary: { evaluating?: boolean };
  paging: {
    pagination: IndexPaginationData;
    buildPageHref: (page: number) => string;
  };
  plan: SubscriptionTier;
  auditsUsed: number;
  auditsRemaining: number;
  auditLimit: number;
  auditsRefreshAt: string | null;
  canStartAudits?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
  onWatchLive: (page: DomainPage) => void;
  onWatchPage: (page: DomainPage) => void;
};

type AuditDialogState = {
  page: DomainPage;
  step: CoverageAuditDialogStep;
  alreadyInFlight?: boolean;
  fullApplicationProbing: boolean;
};

export default function CoverageSection({
  domainId,
  pages,
  summary,
  paging,
  plan,
  auditsUsed,
  auditsRemaining,
  auditLimit,
  auditsRefreshAt,
  canStartAudits = true,
  expanded = true,
  onToggle,
  onWatchLive,
  onWatchPage,
}: Props) {
  const { evaluating = false } = summary;
  const { pagination, buildPageHref } = paging;
  const [pagingBusy, setPagingBusy] = useState(false);
  const [auditingPageId, setAuditingPageId] = useState<string | null>(null);
  const [auditDialog, setAuditDialog] = useState<AuditDialogState | null>(null);
  const [quotaDialogOpen, setQuotaDialogOpen] = useState(false);

  const closeAuditDialog = () => {
    if (auditDialog?.step === "starting") {
      return;
    }

    setAuditDialog(null);
  };

  const startAudit = (page: DomainPage, fullApplicationProbing: boolean) => {
    if (auditingPageId) {
      return;
    }

    setAuditingPageId(page.id);
    setAuditDialog({ page, step: "starting", fullApplicationProbing });
    router.post(
      `/domains/${domainId}/audits`,
      { page_ids: [page.id], full_application_probing: fullApplicationProbing },
      {
        ...domainShowPartialVisit,
        onSuccess: (inertiaPage) => {
          const flash = inertiaVisitFlash(inertiaPage);
          if (flash.error) {
            setAuditDialog(null);
            return;
          }

          setAuditDialog({
            page,
            step: "started",
            alreadyInFlight: Boolean(flash.notice),
            fullApplicationProbing,
          });
        },
        onError: () => setAuditDialog(null),
        onFinish: () => setAuditingPageId(null),
      },
    );
  };

  const confirmAudit = () => {
    const page = auditDialog?.page;
    if (!page || auditDialog.step !== "confirm" || auditingPageId) {
      return;
    }

    startAudit(page, auditDialog.fullApplicationProbing);
  };

  const requestAudit = (page: DomainPage) => {
    if (auditingPageId) {
      return;
    }

    if (auditsRemaining <= 0) {
      setQuotaDialogOpen(true);
      return;
    }

    setAuditDialog({ page, step: "confirm", fullApplicationProbing: page.full_application_probing === true });
  };

  const pagesPagination = (className: string) => (
    <div className={className}>
      <IndexPagination
        pagination={pagination}
        buildHref={buildPageHref}
        only={["pages", "pages_pagination"]}
        preserveScroll
        preserveState
        hideWhenSinglePage
        scrollToId="pages"
        busy={pagingBusy}
        onStart={() => setPagingBusy(true)}
        onFinish={() => setPagingBusy(false)}
      />
    </div>
  );

  return (
    <section id="pages" className="dashboard-section scroll-mt-5">
      <div className="dashboard-section-body">
        <CoverageAccordionHeader
          title="Pages"
          subtitle={evaluating ? "Pages found — evaluating pages now" : "Pages discovered on this domain"}
          expanded={expanded}
          onToggle={onToggle}
          trailing={expanded ? pagesPagination("hidden md:block") : null}
        />

        {expanded && pages.length === 0 ? (
          <p className="mt-6 rounded-lg border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
            No pages discovered yet.
          </p>
        ) : expanded ? (
          <div className="relative mt-6">
            <div
              className={`divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200 bg-white ${
                pagingBusy ? "pointer-events-none opacity-50" : ""
              }`}
              aria-busy={pagingBusy}
            >
              {pages.map((page) => {
                const title = <p className="font-medium break-words text-gray-900">{page.title || page.path}</p>;

                return (
                  <div
                    key={page.id}
                    data-testid="page-row"
                    className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
                  >
                    <div className="min-w-0">
                      {canStartAudits ? (
                        <Link href={page.page_url} className="block transition hover:opacity-80">
                          {title}
                        </Link>
                      ) : (
                        title
                      )}
                      <a
                        href={page.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm break-all text-gray-500 hover:text-primary hover:underline"
                      >
                        {page.path}
                      </a>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 sm:shrink-0 sm:justify-end">
                      <PageStatusLabel page={page} onWatchPage={onWatchPage} />
                      {canStartAudits ? (
                        <button
                          type="button"
                          className="cursor-pointer text-sm font-medium text-primary hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline"
                          disabled={auditingPageId !== null}
                          onClick={() => requestAudit(page)}
                        >
                          {auditingPageId === page.id ? "Starting…" : "Audit page"}
                        </button>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>

            {pagingBusy ? (
              <div className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            ) : null}
          </div>
        ) : null}

        {expanded ? pagesPagination("mt-6 md:hidden") : null}
      </div>

      {canStartAudits ? (
        <>
          <CoverageAuditDialog
            open={auditDialog !== null}
            step={auditDialog?.step ?? "confirm"}
            label={auditDialog?.page.title || auditDialog?.page.path || null}
            alreadyInFlight={Boolean(auditDialog?.alreadyInFlight)}
            fullApplicationProbing={auditDialog?.fullApplicationProbing ?? false}
            auditsUsed={auditsUsed}
            auditLimit={auditLimit}
            auditsRefreshAt={auditsRefreshAt}
            onClose={closeAuditDialog}
            onConfirm={confirmAudit}
            onFullApplicationProbingChange={(fullApplicationProbing) => {
              setAuditDialog((current) => (current ? { ...current, fullApplicationProbing } : current));
            }}
            onWatchLive={() => {
              if (!auditDialog) {
                return;
              }

              onWatchLive(auditDialog.page);
              setAuditDialog(null);
            }}
          />

          <CoverageAuditQuotaDialog
            open={quotaDialogOpen}
            variant={plan === "free" ? "upgrade" : "topup"}
            auditLimit={auditLimit}
            auditsRefreshAt={auditsRefreshAt}
            onDismiss={() => setQuotaDialogOpen(false)}
          />
        </>
      ) : null}
    </section>
  );
}
